import React, { useCallback } from "react";
import { Box } from "@mui/material";
import useDialogModal from "../../../../hooks/useDialogModal";
import EyeExamDialog from "./EyeExamDialog";
import EyeExamEditDialog from "./EyeExamEditDialog";
import EnhancedTable from "../../../enhancedTable/EnhancedTable";
import { EyeExamService } from "../../../../service/eyeExamService";
import useAbortController from "../../../../hooks/useAbortController";
import { toast } from "react-toastify";
import EditIcon from '@mui/icons-material/Edit';
import { formatDateWithTimezone } from "../../../../utils";
import { useAuth } from "../../../../hooks/useAuth";
import { useCan } from "hooks/useCan";

/**
 * EyeExamTab
 * - Lists a patient's eye exams (placeholder for now: in-memory list)
 * - Allows creating, editing, and deleting exams
 * - Opens EyeExamDialog for create/edit
 *
 * Props
 * - customer: { customer: {...}, familyMembers: [...] }
 */
export default function EyeExamTab({ customer }) {
  const [EyeExamDlg, openEyeExamDlg] = useDialogModal(EyeExamDialog);
  const [signal] = useAbortController();
  const { user } = useAuth();
  const can = useCan();
  const canAddEyeExam = can.action('eyeexam', 'create');
  const canDeleteEyeExam = can.action('eyeexam', 'delete');

  const patient = customer?.customer;

  // Direct state management for edit dialog
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editData, setEditData] = React.useState(null);

  const [state, updateState] = React.useReducer((s, p) => ({ ...s, ...p }), {
    data: [],
    totalRowCount: 0,
    pageCount: 0,
    loading: false,
    selected: null,
  });

  const columns = React.useMemo(() => ([
    { Header: 'Exam Date', accessor: 'examDate', enableFilter: true, type: 'date' },
    { Header: 'Practitioner', accessor: 'practitionerName', enableFilter: false, Cell: ({ row }) => row.original?.practitioner?.fullName || '' },
    {
      Header: 'Chief Complaint',
      accessor: 'chiefComplaint',
      enableFilter: true,
      // Prevent long text from stretching the table; show full on hover.
      Cell: ({ value }) => (
        <Box sx={{ maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={value || ''}>
          {value}
        </Box>
      ),
      width: 320,
    },
    { Header: 'Updated', accessor: 'updatedAt', Cell: ({ value }) => formatDateWithTimezone(value, 'PP p') },
  ]), []);

  const fetchDataImpl = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
    const from = pageSize * pageIndex;
    updateState({ loading: true });
    EyeExamService
      .listWithLimit(from, pageSize, filters, sortBy, patient?.id, signal)
      .then((data) => {
        updateState({
          data: data.data,
          totalRowCount: data.count,
          pageCount: Math.ceil(data.count / pageSize),
          loading: false,
        });
      })
      .catch((err) => {
        updateState({ loading: false });
      });
  }, [patient?.id, signal]);

  const handleAdd = () => {
    openEyeExamDlg((evt) => {
      // After save, refresh list
      if (evt?.action === 'save') {
        // trigger EnhancedTable to refetch; easiest is to call fetchDataImpl for first page
        fetchDataImpl({ pageSize: 10, pageIndex: 0, filters: [], sortBy: [] });
      }
    }, { patient });
  };

  const handleEdit = async (row) => {
      const details = await EyeExamService.getById(row.id);
      setEditData(details);
      setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditData(null);
  };

  const handleEditDialogSave = (evt) => {
    if (evt?.action === 'save') {
      fetchDataImpl({ pageSize: 10, pageIndex: 0, filters: [], sortBy: [] });
    }
    handleEditDialogClose();
  };

  const handleDelete = async (rows) => {
    // We’ll soft-delete by voiding for now
    try {
      for (const r of rows) {
        await EyeExamService.void(r.id);
      }
      toast.success('Exam(s) voided');
      fetchDataImpl({ pageSize: 10, pageIndex: 0, filters: [], sortBy: [] });
    } catch (e) {
      // toast is handled by interceptor
    }
  };

  return (
    <Box>
      <EnhancedTable
        columns={columns}
        addRecordHandler={canAddEyeExam ? handleAdd : null}
        deleteRecordHandler={canDeleteEyeExam ? (rowids) => {
          const rows = state.data.filter(d => rowids.includes(d.id));
          handleDelete(rows);
        } : null}
        data={state.data}
        totalRowCount={state.totalRowCount}
        pageCount={state.pageCount}
        fetchData={fetchDataImpl}
        loading={state.loading}
        selectionMode="multi"
        actions={[{ icon: <EditIcon />, onClick: handleEdit, tooltip: 'Edit' }]}
      />
      <EyeExamDlg ignoreBackdropClick patient={patient} practitionerId={user?.id} />
      <EyeExamEditDialog 
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        listener={handleEditDialogSave}
        patient={patient}
        examData={editData}
        practitionerId={user?.id}
      />
    </Box>
  );
}
